/**
 * advance-repair-yard.ts — per-tick hull regen radius for Repair Yards (M14).
 *
 * For each Repair Yard (chassis === 'Repair Yard'), iterate ships in the
 * same system and restore REPAIR_YARD_HULL_PER_TICK to any friendly ship
 * within REPAIR_YARD_RADIUS whose hull is below its current effective max.
 *
 * Scope intentionally narrow:
 *   - Friendly only — no cross-empire healing.
 *   - Hull only — shields get their own regen pass when that lands.
 *   - Repair Yard must be active (not under construction / not cloaked /
 *     not destroyed), but EMP-disabled repair yards still heal (the regen
 *     is automatic infrastructure, not a weapon system).
 *   - Ship-to-yard distance uses the ship's current sx/sy. Mid-jump ships
 *     (systemId null) are skipped — they're in hyperspace.
 *   - Multiple yards stack overlapping coverage; a ship inside two yards
 *     at once gets the regen twice this tick. Matches "friendly ships
 *     within its repair radius slowly regain Hull" — each yard contributes
 *     independently, which feels right: more infrastructure = faster heal.
 *
 * Hull cap read via `effectiveMaxHull(state, empire, tmpl)` so the regen
 * respects per-empire Hull Mk tech exactly like spawning does.
 */

import {
  repairYardHullPerTick,
  repairYardRadius,
} from './balance-space-structures';
import { effectiveMaxHull } from './research-effects';
import type { GameState, ShipInstance, ShipTemplate } from '../state/types/index';

export function advanceRepairYard(state: GameState): void {
  const sd = state.structuresData;
  const radius = repairYardRadius(sd);
  const radiusSq = radius * radius;
  const hullPerTick = repairYardHullPerTick(sd);
  // Collect active Repair Yards up front so the inner friend-ship loop
  // doesn't have to re-filter every iteration.
  const yards: Array<{ ship: ShipInstance; radiusSq: number }> = [];
  for (const ship of state.ships.values()) {
    if (ship.chassis !== 'Repair Yard') continue;
    if (ship.systemId === null) continue;
    if (ship.cloaked) continue;
    if (ship.currentHull <= 0) continue;
    yards.push({ ship, radiusSq });
  }
  if (yards.length === 0) return;

  const ordered = [...state.ships.values()].sort((a, b) => a.id - b.id);
  for (const ship of ordered) {
    if (ship.systemId === null) continue;      // mid-jump
    if (ship.currentHull <= 0) continue;
    const empire = state.empires.get(ship.ownerEmpireId);
    if (!empire) continue;
    const tmpl = getTemplate(state, ship);
    if (!tmpl) continue;
    const maxHull = effectiveMaxHull(state, empire, tmpl);
    if (ship.currentHull >= maxHull) continue;

    for (const { ship: yard, radiusSq: rSq } of yards) {
      if (yard.systemId !== ship.systemId) continue;
      if (yard.ownerEmpireId !== ship.ownerEmpireId) continue; // friendly only
      if (yard.id === ship.id) continue;        // yard doesn't self-heal via its own aura
      const dx = yard.sx - ship.sx;
      const dy = yard.sy - ship.sy;
      if (dx * dx + dy * dy > rSq) continue;
      ship.currentHull = Math.min(maxHull, ship.currentHull + hullPerTick);
      if (ship.currentHull >= maxHull) break;   // capped; no need to check more yards this tick
    }
  }
}

function getTemplate(state: GameState, ship: ShipInstance): ShipTemplate | undefined {
  const empire = state.empires.get(ship.ownerEmpireId);
  if (!empire) return undefined;
  return state.shipTemplates.get(`${empire.raceId}:${ship.chassis}`);
}
